import {
  EVENT_TEMPLATES,
  DEFAULT_TEMPLATE,
  AUTO_FIELDS_SECTION,
} from "./eventTemplates";
import { isPlainObject, toDisplayText, toSafeString } from "./utils";

// Reads a value from a dot-path like "a.b.c"; undefined if any step is missing.
const readPath = (source, dottedPath) => {
  if (typeof dottedPath !== "string") return undefined;
  return dottedPath
    .split(".")
    .reduce(
      (acc, key) =>
        acc !== null && typeof acc === "object" ? acc[key] : undefined,
      source,
    );
};

const isEmpty = (value) => value === undefined || value === null || value === "";

// Returns the first non-empty value across a list of dot-paths.
const firstDefined = (source, paths) => {
  if (!Array.isArray(paths)) return null;
  for (const path of paths) {
    const value = readPath(source, path);
    if (!isEmpty(value)) return value;
  }
  return null;
};

const TEMPLATE_ENTRIES = Object.entries(EVENT_TEMPLATES || {});

const getEventNames = (template) =>
  Array.isArray(template?.eventNames) ? template.eventNames : [];

// Exact name matches win over "PREFIX_*" matches; DEFAULT_TEMPLATE otherwise.
export const findTemplate = (eventName) => {
  const name = toSafeString(eventName).toUpperCase();

  if (name) {
    const exact = TEMPLATE_ENTRIES.find(([, t]) =>
      getEventNames(t).includes(name),
    );
    if (exact) return { key: exact[0], ...exact[1] };

    const prefix = TEMPLATE_ENTRIES.find(([, t]) =>
      getEventNames(t).some(
        (n) =>
          typeof n === "string" &&
          n.endsWith("*") &&
          name.startsWith(n.slice(0, -1)),
      ),
    );
    if (prefix) return { key: prefix[0], ...prefix[1] };
  }

  return { key: "DEFAULT", ...DEFAULT_TEMPLATE };
};

// "customerData.phoneNumber" -> "Phone Number"
const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const looksLikeHtml = (value) =>
  typeof value === "string" && /<\/?[a-z][^>]*>/i.test(value);

// Lists simple (text/number/boolean) values from the payload, skipping arrays and HTML.
const collectAutoFields = (source, { maxFields, maxDepth, skipKeys = [] }) => {
  const rows = [];
  const isSkippedKey = (key) =>
    skipKeys.some((word) => key.toLowerCase().includes(word.toLowerCase()));
  const walk = (node, depth) => {
    if (!isPlainObject(node)) return;
    for (const [key, value] of Object.entries(node)) {
      if (rows.length >= maxFields) return;
      if (isSkippedKey(key)) continue;
      if (value && typeof value === "object") {
        if (depth < maxDepth) walk(value, depth + 1);
      } else if (!looksLikeHtml(value)) {
        const text = toDisplayText(value);
        if (text) rows.push({ label: humanizeKey(key), value: text });
      }
    }
  };
  walk(source, 1);
  return rows;
};

// A bad `value` function in the config must not break the page.
const safeCompute = (fn, payload, event) => {
  try {
    return fn(payload, event);
  } catch (error) {
    console.warn("[LiveEvents] Template value function failed:", error);
    return null;
  }
};

const resolveField = (field, payload, event) => {
  if (!isPlainObject(field)) return null;
  const raw =
    typeof field.value === "function"
      ? safeCompute(field.value, payload, event)
      : firstDefined(payload, field.paths);
  const value = toDisplayText(raw);
  return value
    ? { label: toSafeString(field.label), type: field.type, value }
    : null;
};

const resolveLink = (linkConfig, payload) => {
  if (!isPlainObject(linkConfig)) return null;
  const value = toDisplayText(firstDefined(payload, linkConfig.paths));
  if (!value) return null;
  const href =
    typeof linkConfig.href === "function"
      ? toSafeString(safeCompute(linkConfig.href, value))
      : "";
  return { label: toSafeString(linkConfig.label), value, href: href || null };
};

const resolveList = (listConfig, payload) => {
  if (!isPlainObject(listConfig)) return null;
  const rawItems = firstDefined(payload, listConfig.paths);
  if (!Array.isArray(rawItems)) return null;
  const items = rawItems.filter(isPlainObject).map((item) => ({
    primary: toDisplayText(item[listConfig.primary]),
    secondary: toDisplayText(item[listConfig.secondary]),
  }));
  return items.length
    ? { title: toSafeString(listConfig.title), icon: listConfig.icon, items }
    : null;
};

// Builds panel data for any event using its template, or null if nothing to show.
export const makeEventDetails = (event) => {
  if (!isPlainObject(event)) return null;
  const payload = isPlainObject(event.eventPayload) ? event.eventPayload : null;
  const template = findTemplate(event.eventName);

  const link = resolveLink(template.link, payload);
  let hasPayloadData = !!link;

  const sections = (Array.isArray(template.sections) ? template.sections : [])
    .filter(isPlainObject)
    .map((section) => {
      const fields = Array.isArray(section.fields) ? section.fields : [];
      const rows = fields
        .map((field) => {
          const row = resolveField(field, payload, event);
          // Computed-only rows (like status) don't count as payload data.
          if (row && typeof field.value !== "function") hasPayloadData = true;
          return row;
        })
        .filter(Boolean);
      return { title: toSafeString(section.title), icon: section.icon, rows };
    })
    .filter((section) => section.rows.length);

  const list = resolveList(template.list, payload);
  if (list) hasPayloadData = true;

  // Nothing configured matched - fall back to the payload's own fields.
  if (!hasPayloadData && template.autoFields && payload) {
    const rows = collectAutoFields(payload, AUTO_FIELDS_SECTION);
    if (rows.length) {
      sections.push({
        title: AUTO_FIELDS_SECTION.title,
        icon: AUTO_FIELDS_SECTION.icon,
        rows,
      });
    }
  }

  if (!link && !sections.length && !list) return null;
  return { templateKey: template.key, link, sections, list };
};

export default makeEventDetails;
